/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import AppContext from 'context/Context';
import { jwtDecode } from 'jwt-decode';
import useApi from 'hooks/useApi';
import { debounce, get } from 'lodash';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';
import ServerSideTable from 'components/common/advance-table/ServerSideTable';
import ServerSideTableWrapper from 'components/common/advance-table/ServerSideTableWrapper';
import { getTenantFromToken } from 'utils/routeUtils';

const ReportTable = ({
  data,
  key,
  handleAction,
  showFilter,
  setExportData,
  pageSize,
  setPageSize,
  pageIndex,
  setPageIndex,
  rowCount,
  totalRowCount,
  setRowCount,
  canNextPage,
  filters,
  setFilters,
  globalfilters,
  setGlobalFilters,
  cursor,
  setCursor,
  onSearch
}) => {
  const tenant = getTenantFromToken();
  const [tableData, setTableData] = useState(data);
  const [filteredTableData, setFilteredTableData] = useState(null); // Data to display
  const [globalSearch, setGlobalSearch] = useState('');
  const [paginationFilter, setPaginationFilter] = useState(false);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const flattenedData = tableData;

  const columns =
    flattenedData?.length > 0
      ? [
          ...Object.keys(flattenedData[0])
            .filter(
              key =>
                !key.includes('_color') && key !== 'farm_id' && key !== 'id'
            )
            .map((key, index) => ({
              Header: key
                .replace(/_/g, ' ')
                .replace(/(^\w|\b\w(?![^()]*\)))/g, c => c.toUpperCase()),
              accessor: key,
              Cell: ({ value, row }) => {
                // Dynamically handle NA or null values
                const colorKey = `${key}_color`;
                let displayValue =
                  typeof value === 'object' && value !== null
                    ? ' - '
                    : value === 'NA' || value === null
                    ? ' - '
                    : value;

                return (
                  <span style={{ color: row.original[colorKey] || 'inherit' }}>
                    {displayValue || ''}
                  </span>
                );
              }
            }))
        ]
      : [];

  const handlePageChange = newPageIndex => {
    setPageIndex(newPageIndex);
  };

  const handlePageSizeChange = newPageSize => {
    setPageSize(newPageSize);
    setPageIndex(0);
    setTableData(null);
  };

  // Handle filter changes for specific columns
  const handleFilterChange = (columnId, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
  };

  useEffect(() => {
    // Handle case when filters is undefined or null
    if (!tableData || Object.keys(filters ?? {}).length === 0) {
      setFilteredTableData(tableData); // Reset to original if no filters
      return;
    }

    const filtered = tableData.filter(row =>
      Object.entries(filters).every(([columnId, filterValue]) => {
        const cellValue = row[columnId]?.toString().toLowerCase() || '';
        const filterValueLower = filterValue.toLowerCase();
        return cellValue.startsWith(filterValueLower);
      })
    );
    setExportData(filtered);
    setFilteredTableData(filtered);
    const hasValidFilter = Object.values(filters).some(
      value => value !== null && value !== ''
    );
    hasValidFilter ? setPaginationFilter(true) : setPaginationFilter(false);
    showFilter && hasValidFilter
      ? setRowCount(filtered?.length)
      : setRowCount(totalRowCount);
    console.log('totalrownc', totalRowCount, filtered?.length);
  }, [filters, tableData, showFilter]);

  useEffect(() => {
    if (!showFilter) {
      const clearedFilters = Object.keys(filters).reduce((acc, key) => {
        acc[key] = ''; // Reset each filter to an empty string
        return acc;
      }, {});
      setFilters(clearedFilters);
    }
  }, [showFilter]);

  const handleGlobalFilterChange = (columnId, value) => {
    setGlobalFilters(prevFilters => ({
      ...prevFilters,
      [columnId]: value
    }));
  };

  function handleActionClick(action, rowData) {
    if (handleAction) {
      handleAction(action, rowData); // Triggering the passed action
    } else {
      console.error('handleAction is undefined!');
    }
  }

  // const handleGlobalSearch = e => {
  //   const searchValue = e.target.value;
  //   handleGlobalFilterChange('search_text', searchValue);
  // };

  const handleGlobalSearch = useCallback(
    debounce(searchText => {
      handleGlobalFilterChange('search_text', searchText);
      setGlobalSearch(searchText);
    }, 300),
    [] // Empty dependency array ensures the debounce function is created once
  );

  return (
    <ServerSideTableWrapper
      key={`${key}-${pageIndex}`}
      columns={columns}
      data={filteredTableData || flattenedData}
      pagination
      sortable
      perPage={pageSize}
      className="min-vh-50 py-5"
      stickyColumns={[]}
    >
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
        <input
          type="text"
          placeholder="Search..."
          className="form-control mb-3 mb-md-0 w-100 w-md-25"
          // value={globalfilters.search_text}
          onChange={e => handleGlobalSearch(e.target.value)}
        />
      </div>

      {rowCount > 0 || (Object.keys(filters ?? {}).length > 0 && showFilter) ? (
        <ServerSideTable
          key={`${key}-${pageIndex}`}
          table
          headerClassName="bg-200 text-900 text-nowrap align-middle"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{
            bordered: true,
            striped: true,
            className: 'fs--1 mb-0'
          }}
          filters={filters}
          handleFilterChange={handleFilterChange}
          showFilterRow={showFilter}
          pageIndex={pageIndex}
          pageSize={pageSize}
          cursor={cursor}
          clientSideSearch={true}
          handleAction={handleActionClick}
        />
      ) : (
        <h1
          className="text-center my-5"
          style={{ color: '#6c757d', fontSize: '1.5rem', opacity: 0.7 }}
        >
          {globalSearch != ''
            ? `"${globalSearch}", not found!`
            : 'No data added!'}
        </h1>
      )}

      <div className="mt-3">
        <ServerSideTableFooter
          rowCount={rowCount}
          pageSize={pageSize}
          pageIndex={pageIndex}
          paginationFilter={paginationFilter}
          setPageSize={handlePageSizeChange}
          canPreviousPage={pageIndex > 0}
          canNextPage={canNextPage}
          nextPage={() => handlePageChange(pageIndex + 1)}
          previousPage={() => handlePageChange(Math.max(0, pageIndex - 1))}
          rowsPerPageSelection
          navButtons
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </div>
    </ServerSideTableWrapper>
  );
};

ReportTable.propTypes = {
  data: PropTypes.array.isRequired,
  deletePorjectAPI: PropTypes.object.isRequired,
  startProcessingAPI: PropTypes.object.isRequired
};

ServerSideTableWrapper.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      Cell: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          value: PropTypes.func.isRequired,
          row: PropTypes.shape({
            original: PropTypes.object.isRequired
          }).isRequired
        })
      ])
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  pagination: PropTypes.bool,
  perPage: PropTypes.number,
  handleActions: PropTypes.func.isRequired
};

export default ReportTable;
